/**
 * SceneInsightsDrawer dumb view — renders the Drawer + scene cards given
 * already-loaded data. The smart wrapper at ../index.tsx fetches visuals
 * and wires the SSE invalidation.
 */
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconMicroscope, IconPhoto, IconSparkles } from "@tabler/icons-react"
import type { ISceneImage } from "../interfaces"
import styles from "../styles.module.css"
import type { IViewProps, TSceneTone } from "./interfaces"

/**
 * Map each scene type onto a Mantine semantic palette so the badge picks
 * up the active brand instead of locking to a literal hue. The mapping
 * preserves the design's "tells the story at a glance" intent:
 *   exploration → success, combat → danger, travel → info,
 *   roleplay → brand (the voice work), social → highlight (special),
 *   puzzle → warning, rest → gray (calm, off the mainline).
 */
const SCENE_TYPE_TONES: Record<string, TSceneTone> = {
  exploration: "success",
  combat: "danger",
  travel: "info",
  roleplay: "brand",
  social: "highlight",
  puzzle: "warning",
  rest: "gray",
}

const SCENE_TYPE_LABELS: Record<string, string> = {
  exploration: "Exploration",
  combat: "Combat",
  travel: "Travel",
  roleplay: "Roleplay",
  social: "Social",
  puzzle: "Puzzle",
  rest: "Rest",
}

function SceneInspectModal({
  scene,
  opened,
  onClose,
}: {
  scene: ISceneImage
  opened: boolean
  onClose: () => void
}): React.JSX.Element {
  const { i18n } = useLingui()
  const title = scene.content.title ?? i18n._(msg`Scene ${(scene.ordinal ?? 0) + 1}`)
  const refs = scene.content.referenceImages ?? []

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Stack gap="md">
        {scene.content.description && (
          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              <Trans>Story excerpt</Trans>
            </Text>
            <Text size="sm" fs="italic">
              {scene.content.description}
            </Text>
          </Box>
        )}

        {scene.content.prompt && (
          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              <Trans>Image prompt</Trans>
            </Text>
            <Text size="sm" className={styles.promptText}>
              {scene.content.prompt}
            </Text>
          </Box>
        )}

        {refs.length > 0 && (
          <Box>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>
              <Trans>Reference images used</Trans>
            </Text>
            <Group gap="sm" wrap="wrap">
              {refs.map((url) => (
                <img key={url} src={url} alt={i18n._(msg`Character reference`)} className={styles.refThumb} />
              ))}
            </Group>
          </Box>
        )}

        {scene.content.provider && (
          <Text size="xs" c="dimmed">
            <Trans>Generated via {scene.content.provider}</Trans>
          </Text>
        )}
      </Stack>
    </Modal>
  )
}

function SceneCard({
  scene,
  onImageClick,
}: {
  scene: ISceneImage
  onImageClick: (url: string, alt: string) => void
}): React.JSX.Element {
  const { i18n } = useLingui()
  const [inspectOpened, { open: openInspect, close: closeInspect }] = useDisclosure(false)
  const sceneType = scene.content.type
  const title = scene.content.title ?? i18n._(msg`Scene ${(scene.ordinal ?? 0) + 1}`)
  const characters = scene.content.charactersInvolved ?? []
  const SCENE_TYPE_TRANSLATIONS: Record<string, string> = {
    exploration: i18n._(msg`Exploration`),
    combat: i18n._(msg`Combat`),
    travel: i18n._(msg`Travel`),
    roleplay: i18n._(msg`Roleplay`),
    social: i18n._(msg`Social`),
    puzzle: i18n._(msg`Puzzle`),
    rest: i18n._(msg`Rest`),
  }

  return (
    <Box className={styles.sceneCard}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm" className={styles.sceneTitle} lineClamp={1}>
            {title}
          </Text>
          {sceneType && (
            <Badge size="xs" color={SCENE_TYPE_TONES[sceneType] ?? "gray"} variant="light">
              {SCENE_TYPE_TRANSLATIONS[sceneType] ?? SCENE_TYPE_LABELS[sceneType] ?? sceneType}
            </Badge>
          )}
        </Group>

        {/* biome-ignore lint/a11y/useKeyWithClickEvents: image lightbox, ESC handled by Modal */}
        <img
          src={scene.content.url}
          alt={title}
          className={styles.sceneImage}
          loading="lazy"
          onClick={() => onImageClick(scene.content.url, title)}
        />

        {scene.content.description && (
          <Text size="xs" c="dimmed" fs="italic" lineClamp={3}>
            {scene.content.description}
          </Text>
        )}

        {characters.length > 0 && (
          <Group gap={4} wrap="wrap">
            <Text size="xs" c="dimmed">
              <Trans>Characters:</Trans>
            </Text>
            {characters.map((name) => (
              <span key={name} className={styles.characterChip}>
                {name}
              </span>
            ))}
          </Group>
        )}

        <Group justify="flex-end">
          <Button
            size="compact-xs"
            variant="subtle"
            color="dimmed"
            leftSection={<IconMicroscope size={12} />}
            onClick={openInspect}
          >
            <Trans>Inspect</Trans>
          </Button>
        </Group>
      </Stack>

      <SceneInspectModal scene={scene} opened={inspectOpened} onClose={closeInspect} />
    </Box>
  )
}

export function SceneInsightsDrawerView({
  opened,
  onClose,
  visuals,
  isLoading,
  isLive,
  onImageClick,
  lightboxModal,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()
  const sorted = [...visuals].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon size="sm" variant="transparent" color="brand">
            <IconSparkles size={16} />
          </ThemeIcon>
          <Text fw={600} size="sm">
            <Trans>Scene insights</Trans>
          </Text>
          {isLive && (
            <Box className={styles.liveIndicator}>
              <Badge size="xs" color="brand" variant="dot">
                <Trans>Live</Trans>
              </Badge>
            </Box>
          )}
        </Group>
      }
      position="right"
      size="md"
    >
      <ScrollArea h="calc(100vh - 80px)" offsetScrollbars>
        {isLoading && (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              <Trans>Loading scenes…</Trans>
            </Text>
          </Group>
        )}

        {!isLoading && sorted.length === 0 && (
          <Stack align="center" py="xl" gap="sm">
            <ThemeIcon size="xl" variant="light" color="gray" radius="xl">
              <IconPhoto size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">
              {isLive
                ? i18n._(msg`Waiting for scene images to generate…`)
                : i18n._(msg`No scene images were generated for this session.`)}
            </Text>
            {isLive && (
              <Text size="xs" c="dimmed" ta="center">
                <Trans>Images appear here as each scene is processed.</Trans>
              </Text>
            )}
          </Stack>
        )}

        {sorted.length > 0 && (
          <Stack gap="md">
            <Text size="xs" c="dimmed">
              <Trans>
                {sorted.length} scene{sorted.length !== 1 ? "s" : ""} generated
              </Trans>
            </Text>
            {sorted.map((scene, idx) => (
              <Box key={scene.id}>
                <SceneCard scene={scene} onImageClick={onImageClick} />
                {idx < sorted.length - 1 && <Divider mt="md" />}
              </Box>
            ))}
          </Stack>
        )}
      </ScrollArea>
      {lightboxModal}
    </Drawer>
  )
}
